import React, { ReactElement, useCallback } from "react";

import { color } from "minimum-ui/tokens";
import styled, { css } from "styled-components";
import { H4R } from "typography/headers";

import Checkbox from "../../atoms/Checkbox";
import Radio from "../../atoms/Radio";

export interface OptionProps {
  value: string;
  label: string;
  multi: boolean;
  selected: boolean;
  onSelect: (value: string) => void;
  bold?: boolean;
  basic?: boolean;
  disabled?: boolean;
  icon?: ReactElement | "";
  translate: (key: string, options?: Record<string, string | number>) => string;
}

const Option: React.FC<OptionProps> = ({
  value,
  label,
  multi,
  selected,
  onSelect,
  bold = false,
  basic = false,
  disabled = false,
  icon,
  translate,
}) => {
  const handleChange = useCallback(() => {
    onSelect(value);
  }, [value, onSelect]);

  const UI = multi ? Checkbox : Radio;
  return basic ? (
    <BasicWrapper $selected={selected} $bold={false} onClick={handleChange}>
      <Label $disabled={disabled} as="span">
        {label}
      </Label>
      {icon && icon}
      {selected && (
        <CurrentLabel>
          {translate("current", { namespace: "DEFAULT" })}
        </CurrentLabel>
      )}
    </BasicWrapper>
  ) : (
    <OptionWrapper $bold={bold}>
      <UI active={selected} />
      <Control
        type={multi ? "checkbox" : "radio"}
        onChange={handleChange}
        checked={selected}
      />
      <Label as="span">{label}</Label>
    </OptionWrapper>
  );
};

const Control = styled.input`
  position: absolute;
  opacity: 0;
  left: -200px;
  height: -200px;
`;
const OptionWrapper = styled.label<{ $bold: boolean }>`
  cursor: pointer;
  position: relative;
  display: flex;
  height: 40px;
  align-items: center;
  gap: 16px;
  padding: 0px 24px;
  white-space: nowrap;
  text-overflow: ellipsis;

  ${(props) =>
    props.$bold &&
    css`
      ${H4R} {
        font-weight: 600;
      }
    `}
`;
const BasicWrapper = styled(OptionWrapper)<{
  $selected: boolean;
}>`
  background: ${color.basic.white};

  &:hover {
    background: ${color.neutral[100]};
  }

  ${(props) =>
    props.$selected &&
    css`
      background: ${color.neutral[200]};
      border-left: 2px solid ${color.blue[300]};
      &:hover {
        background: ${color.neutral[200]};
      }
    `};
`;
const CurrentLabel = styled(H4R)`
  color: ${color.neutral[400]};
  font-size: 12px;
  margin-left: auto;
`;

const Label = styled(H4R)<{ $disabled?: boolean }>`
  padding-right: 12px;

  ${(props) =>
    props.$disabled &&
    css`
      --border: ${color.foreground.disabled} !important;
      --background: ${color.neutral[100]} !important;
      --badge: ${color.foreground.disabled} !important;
      --text: ${color.foreground.disabled} !important;
      --icon: ${color.foreground.disabled} !important;
      cursor: not-allowed;
      opacity: 0.5;
    `}
`;

export default Option;
